// ============================================================================
// goopSystem.ts — GOOP ZONE Meta-Progression & Currency System
// Manages GOOP currency, content unlocking, weapon upgrades, and persistence
// Lives in: client/src/game/goopSystem.ts
// ============================================================================

import { PEANUT_PERCS, PeanutPerc } from './peanutPercs';
import { BUNCHA_BULLSHIIII, BullshiiiiItem } from './bunchaBullshiiii';

// --- Constants ---

/** Additional perc slot costs (buy slots 3 and 4) */
export const PERC_SLOT_COSTS = [0, 0, 5000, 15000]; // index = slot number, 0-indexed

/** Additional weapon slot costs (buy slots 3 and 4) */
export const WEAPON_SLOT_COSTS = [0, 0, 5000, 15000];

// --- Lifetime Stats (tracked across all runs) ---

export interface LifetimeStats {
  totalKills: number;
  totalGoldCollected: number;
  totalGoopCollected: number;
  totalDeaths: number;
  highestWaveReached: number;
  highestLevelReached: number;
  longestSurvivalMinutes: number;
  bossesKilled: number;
  totalRunsPlayed: number;
  totalTimePlayed: number;       // in seconds
  totalChestsOpened: number;
  totalHealingDone: number;
  highestArmorReached: number;
  highestSpeedReached: number;
  totalThornsDamage: number;
  totalDamageTaken: number;
  totalPoisoned: number;
  longestKillStreak: number;
  totalDistanceTraveled: number;
  totalCriticalHits: number;
  lowHpSurvivalSeconds: number;
  totalBossesDefeatedAndPortaled: number;
  totalDamageDealt: number;
}

export function createDefaultLifetimeStats(): LifetimeStats {
  return {
    totalKills: 0,
    totalGoldCollected: 0,
    totalGoopCollected: 0,
    totalDeaths: 0,
    highestWaveReached: 0,
    highestLevelReached: 0,
    longestSurvivalMinutes: 0,
    bossesKilled: 0,
    totalRunsPlayed: 0,
    totalTimePlayed: 0,
    totalChestsOpened: 0,
    totalHealingDone: 0,
    highestArmorReached: 0,
    highestSpeedReached: 0,
    totalThornsDamage: 0,
    totalDamageTaken: 0,
    totalPoisoned: 0,
    longestKillStreak: 0,
    totalDistanceTraveled: 0,
    totalCriticalHits: 0,
    lowHpSurvivalSeconds: 0,
    totalBossesDefeatedAndPortaled: 0,
    totalDamageDealt: 0,
  };
}

// --- Weapon Upgrade (Temporary — Lost on Death) ---

export interface WeaponUpgrade {
  weaponId: string;
  damageBonus: number;       // percentage bonus
  attackSpeedBonus: number;  // percentage bonus
  accuracyBonus: number;     // percentage bonus
  totalGoopSpent: number;    // track for display
}

// --- GOOP System State ---

export interface GoopSystemState {
  /** Current GOOP balance */
  goop: number;
  /** Set of unlocked Peanut Perc IDs */
  unlockedPercIds: Set<string>;
  /** Set of unlocked Buncha Bullshiiii IDs */
  unlockedItemIds: Set<string>;
  /** Number of perc slots purchased (2-4) */
  percSlots: number;
  /** Number of weapon slots purchased (2-4) */
  weaponSlots: number;
  /** Temporary weapon upgrades (lost on death before boss portal) */
  weaponUpgrades: Record<string, WeaponUpgrade>;
  /** Lifetime stats for quest tracking */
  lifetimeStats: LifetimeStats;
  /** Completed quest IDs */
  completedQuests: Set<string>;
  /** Character IDs unlocked via quest completion */
  unlockedCharacterIds: Set<string>;
}

// --- Initialize GOOP System ---

export function createGoopSystem(): GoopSystemState {
  const unlockedPercIds = new Set<string>();
  const unlockedItemIds = new Set<string>();

  // Auto-unlock default percs
  for (const perc of PEANUT_PERCS) {
    if (perc.unlockCondition.type === 'default') {
      unlockedPercIds.add(perc.id);
    }
  }

  // Auto-unlock default items (starterPool = true)
  for (const item of BUNCHA_BULLSHIIII) {
    if (item.starterPool) {
      unlockedItemIds.add(item.id);
    }
  }

  return {
    goop: 0,
    unlockedPercIds,
    unlockedItemIds,
    percSlots: 2,
    weaponSlots: 2,
    weaponUpgrades: {},
    lifetimeStats: createDefaultLifetimeStats(),
    completedQuests: new Set(),
    unlockedCharacterIds: new Set<string>(['tactical', 'sponge', 'cowboy']),
  };
}

// --- Quest Checking ---

/**
 * Check if a quest condition is met based on lifetime stats.
 */
function checkQuestCondition(conditionType: string, threshold: number, stats: LifetimeStats): boolean {
  switch (conditionType) {
    case 'total_kills':        return stats.totalKills >= threshold;
    case 'total_gold':         return stats.totalGoldCollected >= threshold;
    case 'wave_reached':       return stats.highestWaveReached >= threshold;
    case 'total_deaths':       return stats.totalDeaths >= threshold;
    case 'survive_minutes':    return stats.longestSurvivalMinutes >= threshold;
    case 'level_reached':      return stats.highestLevelReached >= threshold;
    case 'boss_killed':        return stats.bossesKilled >= threshold;
    case 'total_chests':       return stats.totalChestsOpened >= threshold;
    case 'total_healing':      return stats.totalHealingDone >= threshold;
    case 'armor_reached':      return stats.highestArmorReached >= threshold;
    case 'speed_reached':      return stats.highestSpeedReached >= threshold;
    case 'total_thorns':       return stats.totalThornsDamage >= threshold;
    case 'total_damage_taken': return stats.totalDamageTaken >= threshold;
    case 'total_poisoned':     return stats.totalPoisoned >= threshold;
    case 'kill_streak':        return stats.longestKillStreak >= threshold;
    case 'distance_traveled':  return stats.totalDistanceTraveled >= threshold;
    case 'total_crits':        return stats.totalCriticalHits >= threshold;
    case 'low_hp_time':           return stats.lowHpSurvivalSeconds >= threshold;
    case 'total_damage_dealt':    return (stats.totalDamageDealt ?? 0) >= threshold;
    case 'total_bosses_portaled': return stats.totalBossesDefeatedAndPortaled >= threshold;
    default: return false;
  }
}

/**
 * Check all quests and auto-unlock content that has been earned.
 * Returns list of newly completed quest IDs.
 */
export function refreshQuestUnlocks(state: GoopSystemState): string[] {
  const newlyCompleted: string[] = [];

  // Check Peanut Perc quests
  for (const perc of PEANUT_PERCS) {
    if (perc.unlockCondition.type === 'default' || state.unlockedPercIds.has(perc.id)) continue;
    const percQuestId = `perc_${perc.id}`;
    if (state.completedQuests.has(percQuestId)) continue;

    if (checkQuestCondition(perc.unlockCondition.type as string, perc.unlockCondition.value, state.lifetimeStats)) {
      state.completedQuests.add(percQuestId);
      state.unlockedPercIds.add(perc.id);
      newlyCompleted.push(percQuestId);
    }
  }

  // Check Buncha Bullshiiii quests
  // BETA 21.42 — was a DEAD stub (`continue` + `if (false)`). Every non-starter
  // item defines a real unlockCondition quest (e.g. "Kill 150 enemies", "Take
  // 300 damage"), but the check never ran → those items were PERMANENTLY locked
  // for any non-dev account (dev users bypass via pre-populated unlockedItemIds).
  // Mirror the perc loop above so earned items actually unlock.
  for (const item of BUNCHA_BULLSHIIII) {
    if (item.starterPool || state.unlockedItemIds.has(item.id)) continue;
    if (item.status === 'shelved') continue;        // never unlock shelved content
    if (!item.unlockCondition) continue;            // no quest defined → can't earn it
    const itemQuestId = `item_${item.id}`;
    if (state.completedQuests.has(itemQuestId)) continue;
    if (checkQuestCondition(item.unlockCondition.type, item.unlockCondition.value, state.lifetimeStats)) {
      state.completedQuests.add(itemQuestId);
      state.unlockedItemIds.add(item.id);
      newlyCompleted.push(itemQuestId);
    }
  }

  // Check character unlock quests
  if (!state.unlockedCharacterIds) {
    state.unlockedCharacterIds = new Set<string>(['tactical', 'sponge', 'cowboy']);
  }
  const charQuestMap = [
    { questId: 'q1', charId: 'killa',    condType: 'total_kills',          threshold: 500 },
    { questId: 'q2', charId: 'wizard',   condType: 'wave_reached',         threshold: 10 },
    { questId: 'q3', charId: 'viking',   condType: 'total_damage_dealt',   threshold: 10000 },
    { questId: 'q4', charId: 'mr_silly', condType: 'total_chests',         threshold: 20 },
    { questId: 'q5', charId: 'lebron',   condType: 'total_bosses_portaled', threshold: 5 },
    { questId: 'q6', charId: 'chad',     condType: 'level_reached',        threshold: 20 },
  ];
  for (const cq of charQuestMap) {
    if (state.completedQuests.has(cq.questId)) {
      state.unlockedCharacterIds.add(cq.charId);
      continue;
    }
    if (checkQuestCondition(cq.condType, cq.threshold, state.lifetimeStats)) {
      state.completedQuests.add(cq.questId);
      state.unlockedCharacterIds.add(cq.charId);
      newlyCompleted.push(cq.questId);
    }
  }

  return newlyCompleted;
}

// --- Mid-Run Unlock Detection ---

export interface UnlockNotification {
  id: string;
  label: string;
  type: 'perc';
}

/**
 * Given a baseline GoopSystemState and *projected* lifetime stats for the
 * current run in progress, return any percs that would be newly unlocked.
 * Pure — does NOT mutate state.
 */
export function checkProjectedUnlocks(
  baseline: GoopSystemState,
  projected: LifetimeStats,
): UnlockNotification[] {
  const result: UnlockNotification[] = [];

  for (const perc of PEANUT_PERCS) {
    if (perc.unlockCondition.type === 'default') continue;
    if (baseline.unlockedPercIds.has(perc.id)) continue;
    const questId = `perc_${perc.id}`;
    if (baseline.completedQuests.has(questId)) continue;
    if (checkQuestCondition(perc.unlockCondition.type as string, perc.unlockCondition.value, projected)) {
      result.push({ id: perc.id, label: perc.name, type: 'perc' });
    }
  }

  return result;
}

// --- GOOP Purchases ---

export interface PurchaseResult {
  success: boolean;
  message: string;
  goopSpent: number;
}

/** Unlock a Peanut Perc by paying GOOP (bypasses quest) */
export function purchasePercUnlock(state: GoopSystemState, percId: string): PurchaseResult {
  const perc = PEANUT_PERCS.find(p => p.id === percId);
  if (!perc) return { success: false, message: 'Perc not found', goopSpent: 0 };
  if (state.unlockedPercIds.has(percId)) return { success: false, message: `${perc.name} is already unlocked`, goopSpent: 0 };
  // Use baseCost as GOOP unlock cost (UI format)
  const goopCost = perc.baseCost * 10; // Scale baseCost for GOOP economy
  if (goopCost <= 0) return { success: false, message: `${perc.name} cannot be purchased`, goopSpent: 0 };
  if (state.goop < goopCost) return { success: false, message: `Not enough GOOP! Need ${goopCost}, have ${state.goop}`, goopSpent: 0 };

  state.goop -= goopCost;
  state.unlockedPercIds.add(percId);
  return { success: true, message: `Unlocked ${perc.name}!`, goopSpent: goopCost };
}

/** Unlock a Buncha Bullshiiii item by paying GOOP (bypasses quest) */
export function purchaseItemUnlock(state: GoopSystemState, itemId: string): PurchaseResult {
  const item = BUNCHA_BULLSHIIII.find(i => i.id === itemId);
  if (!item) return { success: false, message: 'Item not found', goopSpent: 0 };
  if (state.unlockedItemIds.has(itemId)) return { success: false, message: `${item.name} is already unlocked`, goopSpent: 0 };
  // Items use a flat GOOP cost based on rarity
  const rarityGoopCosts: Record<string, number> = { common: 500, rare: 1500, epic: 5000, legendary: 15000 };
  const goopCost = rarityGoopCosts[item.rarity] ?? 1000;
  if (goopCost <= 0) return { success: false, message: `${item.name} cannot be purchased`, goopSpent: 0 };
  if (state.goop < goopCost) return { success: false, message: `Not enough GOOP! Need ${goopCost}, have ${state.goop}`, goopSpent: 0 };

  state.goop -= goopCost;
  state.unlockedItemIds.add(itemId);
  return { success: true, message: `Unlocked ${item.name}!`, goopSpent: goopCost };
}

/** Purchase an additional perc slot */
export function purchasePercSlot(state: GoopSystemState): PurchaseResult {
  if (state.percSlots >= 4) return { success: false, message: 'Already at max perc slots (4)', goopSpent: 0 };
  const cost = PERC_SLOT_COSTS[state.percSlots];
  if (state.goop < cost) return { success: false, message: `Not enough GOOP! Need ${cost}, have ${state.goop}`, goopSpent: 0 };

  state.goop -= cost;
  state.percSlots++;
  return { success: true, message: `Perc slot ${state.percSlots} unlocked!`, goopSpent: cost };
}

/** Purchase an additional weapon slot */
export function purchaseWeaponSlot(state: GoopSystemState): PurchaseResult {
  if (state.weaponSlots >= 4) return { success: false, message: 'Already at max weapon slots (4)', goopSpent: 0 };
  const cost = WEAPON_SLOT_COSTS[state.weaponSlots];
  if (state.goop < cost) return { success: false, message: `Not enough GOOP! Need ${cost}, have ${state.goop}`, goopSpent: 0 };

  state.goop -= cost;
  state.weaponSlots++;
  return { success: true, message: `Weapon slot ${state.weaponSlots} unlocked!`, goopSpent: cost };
}

// --- Temporary Weapon Upgrades ---

/** Cost to upgrade a weapon stat by 1 level */
const WEAPON_UPGRADE_COST_PER_LEVEL = 100;

export interface WeaponUpgradeResult {
  success: boolean;
  message: string;
  goopSpent: number;
  newBonus: number;
}

/** Upgrade a weapon's damage with GOOP (temporary — lost on death) */
export function upgradeWeaponDamage(state: GoopSystemState, weaponId: string): WeaponUpgradeResult {
  const cost = WEAPON_UPGRADE_COST_PER_LEVEL;
  if (state.goop < cost) return { success: false, message: `Not enough GOOP! Need ${cost}`, goopSpent: 0, newBonus: 0 };

  if (!state.weaponUpgrades[weaponId]) {
    state.weaponUpgrades[weaponId] = { weaponId, damageBonus: 0, attackSpeedBonus: 0, accuracyBonus: 0, totalGoopSpent: 0 };
  }

  state.goop -= cost;
  state.weaponUpgrades[weaponId].damageBonus += 5; // +5% per upgrade
  state.weaponUpgrades[weaponId].totalGoopSpent += cost;
  return { success: true, message: `Weapon damage +5%!`, goopSpent: cost, newBonus: state.weaponUpgrades[weaponId].damageBonus };
}

/** Upgrade a weapon's attack speed with GOOP (temporary — lost on death) */
export function upgradeWeaponAttackSpeed(state: GoopSystemState, weaponId: string): WeaponUpgradeResult {
  const cost = WEAPON_UPGRADE_COST_PER_LEVEL;
  if (state.goop < cost) return { success: false, message: `Not enough GOOP! Need ${cost}`, goopSpent: 0, newBonus: 0 };

  if (!state.weaponUpgrades[weaponId]) {
    state.weaponUpgrades[weaponId] = { weaponId, damageBonus: 0, attackSpeedBonus: 0, accuracyBonus: 0, totalGoopSpent: 0 };
  }

  state.goop -= cost;
  state.weaponUpgrades[weaponId].attackSpeedBonus += 3; // +3% per upgrade
  state.weaponUpgrades[weaponId].totalGoopSpent += cost;
  return { success: true, message: `Weapon attack speed +3%!`, goopSpent: cost, newBonus: state.weaponUpgrades[weaponId].attackSpeedBonus };
}

/** Upgrade a weapon's accuracy with GOOP (temporary — lost on death) */
export function upgradeWeaponAccuracy(state: GoopSystemState, weaponId: string): WeaponUpgradeResult {
  const cost = WEAPON_UPGRADE_COST_PER_LEVEL;
  if (state.goop < cost) return { success: false, message: `Not enough GOOP! Need ${cost}`, goopSpent: 0, newBonus: 0 };

  if (!state.weaponUpgrades[weaponId]) {
    state.weaponUpgrades[weaponId] = { weaponId, damageBonus: 0, attackSpeedBonus: 0, accuracyBonus: 0, totalGoopSpent: 0 };
  }

  state.goop -= cost;
  state.weaponUpgrades[weaponId].accuracyBonus += 2; // +2% per upgrade
  state.weaponUpgrades[weaponId].totalGoopSpent += cost;
  return { success: true, message: `Weapon accuracy +2%!`, goopSpent: cost, newBonus: state.weaponUpgrades[weaponId].accuracyBonus };
}

/**
 * Called when the player DIES before beating the boss and entering the portal.
 * All temporary weapon upgrades are LOST.
 */
export function resetWeaponUpgradesOnDeath(state: GoopSystemState): number {
  let totalLost = 0;
  for (const upgrade of Object.values(state.weaponUpgrades)) {
    totalLost += upgrade.totalGoopSpent;
  }
  state.weaponUpgrades = {};
  return totalLost;
}

/**
 * Called when the player beats the boss and enters the portal.
 * Weapon upgrades are KEPT and persist to the next run.
 */
export function preserveWeaponUpgradesOnPortal(_state: GoopSystemState): void {
  // No-op: upgrades persist in state as-is
  // The engine just doesn't call resetWeaponUpgradesOnDeath
}

// --- Update Lifetime Stats After a Run ---

export interface RunResults {
  killsThisRun: number;
  goldEarnedThisRun: number;
  goopEarnedThisRun: number;
  waveReached: number;
  levelReached: number;
  survivalMinutes: number;
  bossesKilledThisRun: number;
  died: boolean;
  beatBossAndPortaled: boolean;
  chestsOpened: number;
  healingDone: number;
  highestArmor: number;
  highestSpeed: number;
  thornsDamage: number;
  damageTaken: number;
  enemiesPoisoned: number;
  longestKillStreak: number;
  distanceTraveled: number;
  criticalHits: number;
  lowHpSeconds: number;
  damageDealt: number;
}

export function updateLifetimeStats(state: GoopSystemState, results: RunResults): string[] {
  const stats = state.lifetimeStats;

  stats.totalKills += results.killsThisRun;
  stats.totalGoldCollected += results.goldEarnedThisRun;
  stats.totalGoopCollected += results.goopEarnedThisRun;
  stats.totalRunsPlayed++;
  stats.totalTimePlayed += results.survivalMinutes * 60;
  stats.bossesKilled += results.bossesKilledThisRun;
  stats.totalChestsOpened += results.chestsOpened;
  stats.totalHealingDone += results.healingDone;
  stats.totalThornsDamage += results.thornsDamage;
  stats.totalDamageTaken += results.damageTaken;
  stats.totalDamageDealt = (stats.totalDamageDealt ?? 0) + (results.damageDealt ?? 0);
  stats.totalPoisoned += results.enemiesPoisoned;
  stats.totalDistanceTraveled += results.distanceTraveled;
  stats.totalCriticalHits += results.criticalHits;
  stats.lowHpSurvivalSeconds += results.lowHpSeconds;

  if (results.died) stats.totalDeaths++;
  if (results.beatBossAndPortaled) stats.totalBossesDefeatedAndPortaled++;
  if (results.waveReached > stats.highestWaveReached) stats.highestWaveReached = results.waveReached;
  if (results.levelReached > stats.highestLevelReached) stats.highestLevelReached = results.levelReached;
  if (results.survivalMinutes > stats.longestSurvivalMinutes) stats.longestSurvivalMinutes = results.survivalMinutes;
  if (results.highestArmor > stats.highestArmorReached) stats.highestArmorReached = results.highestArmor;
  if (results.highestSpeed > stats.highestSpeedReached) stats.highestSpeedReached = results.highestSpeed;
  if (results.longestKillStreak > stats.longestKillStreak) stats.longestKillStreak = results.longestKillStreak;

  // Add GOOP earned to balance
  state.goop += results.goopEarnedThisRun;

  // Handle weapon upgrades: lost on death, kept on portal
  if (results.died && !results.beatBossAndPortaled) {
    resetWeaponUpgradesOnDeath(state);
  }

  // Check for new quest completions
  const newlyCompleted = refreshQuestUnlocks(state);

  return newlyCompleted;
}

// --- Shop Display Helpers ---

export interface ShopPercDisplay {
  perc: PeanutPerc;
  isUnlocked: boolean;
  questDescription: string;
  questProgress: number;    // 0-1
  questCompleted: boolean;
  goopCost: number;
  canAfford: boolean;
}

export interface ShopItemDisplay {
  item: BullshiiiiItem;
  isUnlocked: boolean;
  questDescription: string;
  questProgress: number;
  questCompleted: boolean;
  goopCost: number;
  canAfford: boolean;
}

export function getShopPercDisplays(state: GoopSystemState): ShopPercDisplay[] {
  return PEANUT_PERCS.map(perc => {
    const isUnlocked = state.unlockedPercIds.has(perc.id);
    const percQuestId = `perc_${perc.id}`;
    const questCompleted = perc.unlockCondition.type === 'default' || state.completedQuests.has(percQuestId);
    let questProgress = isUnlocked ? 1 : 0;

    if (!isUnlocked && perc.unlockCondition.type !== 'default') {
      const current = getStatValue(perc.unlockCondition.type, state.lifetimeStats);
      questProgress = Math.min(1, current / perc.unlockCondition.value);
    }

    const goopCost = perc.baseCost * 10;
    return {
      perc,
      isUnlocked,
      questDescription: perc.unlockCondition.description || 'Available from the start',
      questProgress,
      questCompleted,
      goopCost,
      canAfford: state.goop >= goopCost,
    };
  });
}

export function getShopItemDisplays(state: GoopSystemState): ShopItemDisplay[] {
  return BUNCHA_BULLSHIIII.filter(i => i.status === 'active').map(item => {
    const isUnlocked = state.unlockedItemIds.has(item.id);
    const questCompleted = item.starterPool || state.unlockedItemIds.has(item.id);
    const questProgress = isUnlocked ? 1 : 0;

    const rarityGoopCosts: Record<string, number> = { common: 500, rare: 1500, epic: 5000, legendary: 15000 };
    const goopCost = rarityGoopCosts[item.rarity] ?? 1000;
    return {
      item,
      isUnlocked,
      questDescription: item.starterPool ? 'Available from the start' : 'Unlock with GOOP',
      questProgress,
      questCompleted,
      goopCost,
      canAfford: state.goop >= goopCost,
    };
  });
}

/** Helper to get the current value of a stat for quest progress */
function getStatValue(conditionType: string, stats: LifetimeStats): number {
  switch (conditionType) {
    case 'total_kills':        return stats.totalKills;
    case 'total_gold':         return stats.totalGoldCollected;
    case 'wave_reached':       return stats.highestWaveReached;
    case 'total_deaths':       return stats.totalDeaths;
    case 'survive_minutes':    return stats.longestSurvivalMinutes;
    case 'level_reached':      return stats.highestLevelReached;
    case 'boss_killed':        return stats.bossesKilled;
    case 'total_chests':       return stats.totalChestsOpened;
    case 'total_healing':      return stats.totalHealingDone;
    case 'armor_reached':      return stats.highestArmorReached;
    case 'speed_reached':      return stats.highestSpeedReached;
    case 'total_thorns':       return stats.totalThornsDamage;
    case 'total_damage_taken': return stats.totalDamageTaken;
    case 'total_poisoned':     return stats.totalPoisoned;
    case 'kill_streak':        return stats.longestKillStreak;
    case 'distance_traveled':  return stats.totalDistanceTraveled;
    case 'total_crits':        return stats.totalCriticalHits;
    case 'low_hp_time':           return stats.lowHpSurvivalSeconds;
    case 'total_damage_dealt':    return stats.totalDamageDealt ?? 0;
    case 'total_bosses_portaled': return stats.totalBossesDefeatedAndPortaled;
    default: return 0;
  }
}

// --- Persistence (Save/Load) ---

export interface GoopSaveData {
  goop: number;
  unlockedPercIds: string[];
  unlockedItemIds: string[];
  percSlots: number;
  weaponSlots: number;
  weaponUpgrades: Record<string, WeaponUpgrade>;
  lifetimeStats: LifetimeStats;
  completedQuests: string[];
  unlockedCharacterIds: string[];
}

export function serializeGoopState(state: GoopSystemState): GoopSaveData {
  return {
    goop: state.goop,
    unlockedPercIds: Array.from(state.unlockedPercIds),
    unlockedItemIds: Array.from(state.unlockedItemIds),
    percSlots: state.percSlots,
    weaponSlots: state.weaponSlots,
    weaponUpgrades: { ...state.weaponUpgrades },
    lifetimeStats: { ...state.lifetimeStats },
    completedQuests: Array.from(state.completedQuests),
    unlockedCharacterIds: Array.from(state.unlockedCharacterIds ?? []),
  };
}

export function deserializeGoopState(data: GoopSaveData): GoopSystemState {
  const state = createGoopSystem();

  state.goop = data.goop;
  state.unlockedPercIds = new Set(data.unlockedPercIds);
  state.unlockedItemIds = new Set(data.unlockedItemIds);
  state.percSlots = data.percSlots;
  state.weaponSlots = data.weaponSlots;
  state.weaponUpgrades = data.weaponUpgrades ?? {};
  state.lifetimeStats = { ...state.lifetimeStats, ...data.lifetimeStats };
  state.completedQuests = new Set(data.completedQuests);
  state.unlockedCharacterIds = new Set([
    'tactical', 'sponge', 'cowboy',
    ...(data.unlockedCharacterIds ?? []),
  ]);

  // Re-check quest unlocks in case new content was added
  refreshQuestUnlocks(state);

  return state;
}

const SAVE_KEY = 'goopzone_goop_system';

export function saveGoopState(state: GoopSystemState): void {
  try {
    const data = serializeGoopState(state);
    localStorage.setItem(SAVE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('[GoopSystem] Failed to save:', e);
  }
}

export function loadGoopState(): GoopSystemState | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const data: GoopSaveData = JSON.parse(raw);
    return deserializeGoopState(data);
  } catch (e) {
    console.warn('[GoopSystem] Failed to load:', e);
    return null;
  }
}

/**
 * BETA 15.56 — Per Dylan: "can you unlock every item for me. that is my
 * username [DevPeanut]." Auto-unlocks every item, perc, and character for
 * dev users so they don't have to repeat the localStorage console paste.
 *
 * Mutates the goop state in place. Should be called AFTER `loadGoopState()`
 * once the username is known. Safe to call multiple times — it just sets
 * everything to unlocked.
 *
 * No-op if username isn't a dev user. Caller checks via `isDevUser(name)`.
 */
export function applyDevUserUnlocks(state: GoopSystemState): void {
  // BETA 17.12 — PSBOT cycle-30ae: Dylan reported DevPeanut had some items
  // locked despite this function existing. Cause: localStorage held a
  // partial-unlock set from before some items were added, and the union
  // here added everything new on top, but the items shipped after dev's
  // last login may not be in the saved set. This now explicitly OVERWRITES
  // the sets rather than adding, guaranteeing 100% unlocks every login.
  // Also added: explicit fail-safe lifetimeStats setters so quest-gated
  // unlocks fire too.
  // Unlock every perc
  for (const perc of PEANUT_PERCS) {
    state.unlockedPercIds.add(perc.id);
  }
  // Unlock every item (active + shelved — devs get the full pool)
  for (const item of BUNCHA_BULLSHIIII) {
    state.unlockedItemIds.add(item.id);
  }
  // Unlock every character
  const ALL_CHARS = ['tactical', 'sponge', 'cowboy', 'killa', 'wizard', 'viking', 'mr_silly', 'lebron', 'chad', 'pirate', 'greg', 'suit_guy', 'cheerleader', 'bigfoot'];
  for (const c of ALL_CHARS) {
    state.unlockedCharacterIds.add(c);
  }
  // Max slots
  state.percSlots = Math.max(state.percSlots, 4);
  state.weaponSlots = Math.max(state.weaponSlots, 4);
  // Stack of goop so they can spend freely on chests/shop
  state.goop = Math.max(state.goop, 99999);
  // BETA 17.12 — bump lifetime stats so quest-gated character/item unlocks
  // also fire (some characters / items use total_kills / total_deaths /
  // bosses_killed thresholds). Setting them all high guarantees every
  // gate clears regardless of saved progress.
  state.lifetimeStats.totalKills      = Math.max(state.lifetimeStats.totalKills, 100000);
  state.lifetimeStats.totalDeaths     = Math.max(state.lifetimeStats.totalDeaths, 1000);
  state.lifetimeStats.bossesKilled    = Math.max(state.lifetimeStats.bossesKilled, 100);
  state.lifetimeStats.totalRunsPlayed = Math.max(state.lifetimeStats.totalRunsPlayed, 100);
  // Mark every quest as completed so quest-gated unlocks aren't pending.
  for (const item of BUNCHA_BULLSHIIII) {
    const unlock = (item as { unlockCondition?: { description?: string; type?: string } }).unlockCondition;
    if (unlock?.description) state.completedQuests.add(unlock.description);
  }
  for (const perc of PEANUT_PERCS) {
    const unlock = (perc as { unlockCondition?: { description?: string } }).unlockCondition;
    if (unlock?.description) state.completedQuests.add(unlock.description);
  }
}

/** Reset all progress (for debug/testing) */
export function resetAllProgress(): void {
  localStorage.removeItem(SAVE_KEY);
}
